// A month of prayer times under the Athan, Iqamah and Athan-calculation pages.
//   mode="athan"   the saved athan times
//   mode="iqamah"  the saved athan + iqamah times
//   preview={...}  athan times with unsaved calculation settings (calculation page);
//                  null while they are incomplete
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { skipToken } from "@reduxjs/toolkit/query/react";
import PrayerTimeTable, { athanOf, iqamahOf } from "./prayer-time-table";
import PrayerTimeTableSkeleton from "./PrayerTimeTableSkeleton";
import Label from "../../../components/labels/Labels";
import MonthDropdown from "../../../components/dropdown/MonthDropdown";
import { useGetazantimeQuery, useGetiqamahtimesQuery, usePreviewAzanTimesQuery } from "../../../store/Reducer/prayertimes";

const monthOptions = [...Array(12).keys()].map((i) => ({
  id: i + 1,
  name: new Date(0, i).toLocaleString("default", { month: "long" }),
}));

const PrayerTimes = ({ mode, preview, onLoaded }) => {
  const { t } = useTranslation();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[now.getMonth()]);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const args = { year: selectedYear, month: selectedMonth.id };
  const validYear = selectedYear >= 2000 && selectedYear <= 2100;

  const azan = useGetazantimeQuery(mode === "athan" && validYear ? args : skipToken);
  const iqamah = useGetiqamahtimesQuery(mode === "iqamah" && validYear ? args : skipToken);
  const saved = mode === "athan" ? azan : iqamah;

  // The calculation page's preview: the server's calculation with the unsaved choices.
  const previewed = usePreviewAzanTimesQuery(preview && validYear ? { ...args, ...preview } : skipToken);
  const shown = preview ? previewed : saved;

  const data = shown.data?.data;
  const prayerTimes = (mode === "iqamah" ? data?.iqamahTimes : data?.data) || [];
  const loading = shown.isFetching;

  useEffect(() => {
    if (data && onLoaded && !preview) onLoaded(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const error = shown.error;
  useEffect(() => {
    if (error) toast.error(error?.data?.message || "Couldn't load the prayer times.");
  }, [error]);

  const exportToCSV = () => {
    if (!prayerTimes.length) return;
    const prayers = ["Fajr", "Sunrise", "Dhuhr", "Asr", "Maghrib", "Isha"];
    const headers = ["Date", ...prayers.flatMap((p) => (mode === "iqamah" && p !== "Sunrise" ? [`${p} Athan`, `${p} Iqamah`] : [p]))];
    const rows = prayerTimes.map((day) => [
      day.date?.gregorian?.date,
      ...prayers.flatMap((p) => (mode === "iqamah" && p !== "Sunrise" ? [athanOf(day, p), iqamahOf(day, p)] : [athanOf(day, p)])),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `prayer-times-${selectedYear}-${String(selectedMonth.id).padStart(2, "0")}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  return (
    <section className="w-full px-4 mt-6">
      <div className="w-full mx-auto pt-3 pb-5 px-4">
        <h2 className="text-2xl font-semibold text-start text-gray-800">
          {preview ? "Preview" : t("prayer_time_text")}
        </h2>

        {data?.locationMissing && (
          <p role="alert" className="mt-3 py-3 px-3 text-sm rounded-lg border border-amber-400 bg-amber-50 text-amber-800">
            Your masjid's location isn't set, so these times are not for your area.{" "}
            <Link to="/user-dashboard/masjid-information" className="font-semibold underline">Set the location in Masjid information</Link>.
          </p>
        )}

        <div className="w-full mt-3 flex justify-start items-end flex-wrap gap-y-3 gap-x-3">
          <div>
            <Label htmlFor="month" text="Month" />
            <MonthDropdown selectedOption={selectedMonth} options={monthOptions} onSelect={setSelectedMonth} title="Choose month" />
          </div>
          <div>
            <Label htmlFor="year" text="Year" />
            <input
              id="year"
              type="number"
              min="2000"
              max="2100"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-28 py-2.5 px-4 text-sm font-medium rounded-lg border border-gray-400 bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            type="button"
            onClick={exportToCSV}
            disabled={!prayerTimes.length}
            className="px-5 py-2.5 text-sm bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {t("export_to_CSV_button")}
          </button>
        </div>

        <div className="mt-2">
          {loading ? (
            <PrayerTimeTableSkeleton />
          ) : prayerTimes.length ? (
            <PrayerTimeTable prayerTimes={prayerTimes} showIqamah={mode === "iqamah"} />
          ) : (
            <p className="mt-8 text-center text-gray-600">
              {preview === null
                ? "Choose all three settings above to see the times."
                : data?.notice || error?.data?.message || t("nodatafound")}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default PrayerTimes;
